import {
  SUMMER_CAMPAIGN_CATEGORY_IDS,
  SUMMER_CAMPAIGN_TEAM_CATALOG,
  SUMMER_CAMPAIGN_VOLUNTEER_TYPE_OPTIONS,
} from "./summer-campaign";
import {
  allowsGroupVolunteer,
  allowsIndividualVolunteerApp,
} from "./opportunity-volunteer-mode";

export type SummerCampaignLeadMatchReason =
  | "team"
  | "category"
  | "attendance"
  | "availability"
  | "open"
  | "capacity";

export function parseSummerCampaignLeadCsvIds(value: string | null | undefined) {
  if (!value?.trim()) return [];
  return value.split(",").map((id) => id.trim()).filter(Boolean);
}

export function getSummerCampaignTeamName(teamId: string) {
  const entry = SUMMER_CAMPAIGN_TEAM_CATALOG[
    teamId as keyof typeof SUMMER_CAMPAIGN_TEAM_CATALOG
  ];
  return entry?.name ?? teamId;
}

export function getSummerCampaignCategoryLabel(categoryId: string) {
  const entry = Object.entries(SUMMER_CAMPAIGN_CATEGORY_IDS).find(
    ([, id]) => id === categoryId,
  );
  if (!entry) return categoryId;
  const label = SUMMER_CAMPAIGN_VOLUNTEER_TYPE_OPTIONS.find(
    (opt) => opt.value === entry[0],
  )?.label;
  return label ?? categoryId;
}

export function regionLabel(region: string) {
  if (region === "NORTH") return "צפון";
  if (region === "SOUTH") return "דרום";
  if (region === "BOTH") return "צפון ודרום";
  return region;
}

export function attendanceLabel(attendance: string | null | undefined) {
  if (attendance === "GROUP") return "קבוצה";
  if (attendance === "INDIVIDUAL") return "יחיד";
  return attendance ?? "—";
}

export function datesOverlap(
  aStart: Date | null | undefined,
  aEnd: Date | null | undefined,
  bStart: Date | null | undefined,
  bEnd: Date | null | undefined,
) {
  if (!aStart || !aEnd || !bStart || !bEnd) return true;
  return aEnd >= bStart && aStart <= bEnd;
}

export const MATCH_REASON_LABELS: Record<SummerCampaignLeadMatchReason, string> = {
  team: "צוות",
  category: "קטגוריה",
  attendance: "יחיד/קבוצה",
  availability: "זמינות",
  open: "פתוחה",
  capacity: "מקום פנוי",
};

export function volunteerHouseLabel(volunteerHouse: boolean) {
  return volunteerHouse ? "כן" : "לא";
}

export function isManualOpportunityOffLeadPreferences(
  leadTeamIds: string[],
  leadCategoryIds: string[],
  opportunityTeamIds: string[],
  opportunityCategoryIds: string[],
) {
  const teamMatch =
    leadTeamIds.length === 0 ||
    opportunityTeamIds.some((id) => leadTeamIds.includes(id));
  const categoryMatch =
    leadCategoryIds.length === 0 ||
    opportunityCategoryIds.some((id) => leadCategoryIds.includes(id));
  return !teamMatch || !categoryMatch;
}

export function leadAttendanceMatchesVolunteerMode(
  leadAttendance: string | null | undefined,
  volunteerMode?: string | null,
): boolean {
  if (!leadAttendance) return true;
  if (leadAttendance === "GROUP") {
    return allowsGroupVolunteer(volunteerMode);
  }
  if (leadAttendance === "INDIVIDUAL") {
    return allowsIndividualVolunteerApp(volunteerMode);
  }
  return true;
}

export function isLeadOpportunityOffPreferences(
  leadAttendance: string | null | undefined,
  leadTeamIds: string[],
  leadCategoryIds: string[],
  opportunity: {
    volunteerMode?: string | null;
    teamIds: string[];
    categoryIds: string[];
  },
): boolean {
  if (
    isManualOpportunityOffLeadPreferences(
      leadTeamIds,
      leadCategoryIds,
      opportunity.teamIds,
      opportunity.categoryIds,
    )
  ) {
    return true;
  }
  if (!leadAttendance) return false;
  return !leadAttendanceMatchesVolunteerMode(
    leadAttendance,
    opportunity.volunteerMode,
  );
}

/** Maps summer-campaign lead region to group preferred volunteer regions. */
export function leadRegionToGroupVolunteerRegions(
  region: string | null | undefined,
): ("NORTH" | "CENTER" | "SOUTH")[] {
  if (region === "NORTH") return ["NORTH"];
  if (region === "SOUTH") return ["SOUTH"];
  if (region === "BOTH") return ["NORTH", "SOUTH"];
  return ["NORTH", "CENTER", "SOUTH"];
}

export type SummerCampaignLeadMatchInstance = {
  id: string;
  title: string | null;
  startDate: Date | string | null;
  endDate: Date | string | null;
  location: string | null;
  hasTime?: boolean;
  hasStartTime?: boolean;
  timeDescription?: string | null;
  alreadyRegistered: boolean;
  offPreferences?: boolean;
  matchReasons?: SummerCampaignLeadMatchReason[];
  volunteerMode?: string | null;
  source?: "AUTO" | "MANUAL";
};

export type SummerCampaignLeadGroupedMatch = {
  kind: "group";
  id: string;
  title: string | null;
  location: string | null;
  startDate: Date | string | null;
  endDate: Date | string | null;
  offPreferences: boolean;
  matchReasons?: SummerCampaignLeadMatchReason[];
  instances: SummerCampaignLeadMatchInstance[];
};

export type SummerCampaignLeadStandaloneMatch = SummerCampaignLeadMatchInstance & {
  kind: "standalone";
};

export type SummerCampaignLeadMatchItem =
  | SummerCampaignLeadGroupedMatch
  | SummerCampaignLeadStandaloneMatch;

type RawLeadMatchRow = SummerCampaignLeadMatchInstance & {
  parentOpportunityId?: string | null;
  isOpportunityGroupParent?: boolean;
};

export function groupSummerCampaignLeadMatches(
  rows: RawLeadMatchRow[],
  parentTitles: Map<string, string | null>,
): SummerCampaignLeadMatchItem[] {
  const childrenByParent = new Map<string, RawLeadMatchRow[]>();
  for (const row of rows) {
    if (!row.parentOpportunityId) continue;
    const list = childrenByParent.get(row.parentOpportunityId) ?? [];
    list.push(row);
    childrenByParent.set(row.parentOpportunityId, list);
  }

  const groups: SummerCampaignLeadGroupedMatch[] = [];
  for (const [parentId, instances] of childrenByParent) {
    const sorted = [...instances].sort((a, b) => {
      const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
      return aStart - bStart;
    });
    const primary = sorted[0];
    groups.push({
      kind: "group",
      id: parentId,
      title: parentTitles.get(parentId) ?? primary.title,
      location: primary.location ?? null,
      startDate: primary.startDate ?? null,
      endDate: primary.endDate ?? null,
      offPreferences: sorted.some((i) => i.offPreferences),
      matchReasons: primary.matchReasons,
      instances: sorted.map(
        ({ parentOpportunityId: _p, isOpportunityGroupParent: _g, ...inst }) => ({
          ...inst,
          source: inst.source ?? "AUTO",
        }),
      ),
    });
  }

  const groupedParentIds = new Set(childrenByParent.keys());
  const standalones: SummerCampaignLeadStandaloneMatch[] = rows
    .filter(
      (row) =>
        !row.parentOpportunityId &&
        !row.isOpportunityGroupParent &&
        !groupedParentIds.has(row.id),
    )
    .map(({ parentOpportunityId: _p, isOpportunityGroupParent: _g, ...rest }) => ({
      ...rest,
      kind: "standalone" as const,
      source: rest.source ?? "AUTO",
    }));

  return [...groups, ...standalones].sort((a, b) => {
    if (a.offPreferences !== b.offPreferences) {
      return a.offPreferences ? 1 : -1;
    }
    const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
    const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
    return aStart - bStart;
  });
}

/** Flatten grouped matches for selection/badge logic. */
export function flattenSummerCampaignLeadMatches(
  items: SummerCampaignLeadMatchItem[],
): SummerCampaignLeadMatchInstance[] {
  const flat: SummerCampaignLeadMatchInstance[] = [];
  for (const item of items) {
    if (item.kind === "group") {
      flat.push(...item.instances);
    } else {
      flat.push(item);
    }
  }
  return flat;
}
